import React from "react";
import { useTranslation } from "react-i18next";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import { getBusinessCatalogs } from "../../api/administration";
import { GetBusinessCatalogsResponse } from "../../types/requestsOutputs";
import Dropdown from "../../components/common/Dropdown";
import NumberInput from "../../components/common/NumberInput";
import RadioButton from "../../components/common/RadioButton";
import Icon from "../../components/common/Icon";
import { generateTokensBusinessCatalog } from "../../api/support";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import studio from "../../styles/studioPage.module.css";

const TokenGeneration: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [loading, setLoading] = React.useState<boolean>(true);
    const [businessCatalogs, setBusinessCatalogs] = React.useState<GetBusinessCatalogsResponse | null>(null);
    const [chosenBusinessCatalogId, setChosenBusinessCatalogId] = React.useState<string>("");

    const [isUnlimited, setIsUnlimited] = React.useState<boolean>(false);
    const [numberOfCodes, setNumberOfCodes] = React.useState<number>(1);

    const [QRCode, setQRCode] = React.useState<string>("");
    const [QRCodeString, setQRCodeString] = React.useState<string>("");

    const fetchBusinessCatalogs = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getBusinessCatalogs(token);
            setBusinessCatalogs(response);
        } catch (error) {
            console.error("Failed to fetch businessCatalogs", error);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchBusinessCatalogs();
    }, []);

    const businessCatalogsForDropdown = businessCatalogs?.map((businessCatalog) => ({
        label: businessCatalog.internalName || "NA",
        value: businessCatalog.id,
    }));

    const handleGenerate = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }

        if (!chosenBusinessCatalogId) {
            console.error("Aucun BusinessCatalog sélectionné");
            return;
        }

        setLoading(true);

        const response = await generateTokensBusinessCatalog(
            token,
            chosenBusinessCatalogId,
            numberOfCodes,
            isUnlimited,
        );
        setQRCode(response.url);
        setQRCodeString(response.urlString);
        setLoading(false);
    };

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("tokenGeneration")} subtitle={t("tokenGenerationSubTitle")} />
            </div>

            <div className={adminSurface.supportGenForm}>
                <Dropdown
                    options={businessCatalogsForDropdown}
                    placeholder={t("selectBusinessCatalog")}
                    onChange={(value) => setChosenBusinessCatalogId(value)}
                    value={chosenBusinessCatalogId}
                    disabled={false}
                    label=""
                    loading={false}
                    error={false}
                    isWithoutMargin
                />

                <div className={adminSurface.supportGenLbl}>{t("isUnlimited")}</div>
                <div className={adminSurface.supportRadios}>
                    <RadioButton label={t("yes")} checked={isUnlimited} onChange={() => setIsUnlimited(true)} />
                    <RadioButton label={t("no")} checked={!isUnlimited} onChange={() => setIsUnlimited(false)} />
                </div>

                {!isUnlimited && (
                    <NumberInput
                        placeholder={t("numberOfCodes")}
                        value={numberOfCodes}
                        size="medium"
                        onChange={(e) => setNumberOfCodes(parseInt(e.target.value))}
                    />
                )}

                <div>
                    <button
                        className={adminSurface.addButton}
                        onClick={handleGenerate}
                        disabled={!chosenBusinessCatalogId || (!isUnlimited && numberOfCodes < 1)}
                    >
                        {t("generate")}
                    </button>
                </div>

                {loading && <p style={{ marginTop: 24 }}>{t("generating")}...</p>}

                {!loading && QRCode && QRCodeString && (
                    <div className={adminSurface.supportDl}>
                        <div className={adminSurface.supportDlLbl}>{t("downloadFile")}:</div>
                        <div className={adminSurface.supportDlRow}>
                            <a
                                href={QRCode}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className={adminSurface.supportBtnDl}
                            >
                                <Icon name="ic-download" className="ic" size={17} />
                                {t("downloadQRCode")}
                            </a>
                            <a
                                href={QRCodeString}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className={adminSurface.supportBtnDl}
                            >
                                <Icon name="ic-download" className="ic" size={17} />
                                {t("downloadQRCodeString")}
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TokenGeneration;
